import { useState, useEffect } from 'react';
import {
  DollarSign, ArrowUpRight, ArrowDownRight, Download, Loader2, RefreshCw, Clock, AlertTriangle,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';

const RANGE_MONTHS = { '3m': 3, '6m': 6, '12m': 12 };

// Upper bound for both date pickers — there's no revenue from the future.
const todayIso = new Date().toISOString().slice(0, 10);

export default function Analytics() {
  const { hasPermission, isSuperAdmin } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fixed 12-month platform series, used only to derive Yearly Run Rate —
  // that's a specific named metric (not "total for the selected range"), so
  // it and Monthly Recurring Revenue / Avg Revenue per Café stay tied to the
  // dashboard's own MTD figures regardless of the range picker below (same
  // "current-state numbers don't get re-scoped" call Billing.jsx makes for
  // Total cafés / Active licenses / Expiring soon).
  const [fullRevenueSeries, setFullRevenueSeries] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const canExport = hasPermission(PERMISSIONS.EXPORT_REPORTS) || isSuperAdmin();

  // ONE range control (in the page header, after Export) drives everything
  // else: the Revenue Trend chart, Top Revenue Cafés, and Café Revenue
  // Trend table — all fetched together for the same window so they can
  // never disagree, replacing what used to be two independent pickers (one
  // per section) that could show different ranges from each other.
  const [analyticsRange, setAnalyticsRange] = useState('6m');
  const [customRangeStart, setCustomRangeStart] = useState('');
  const [customRangeEnd, setCustomRangeEnd] = useState('');
  const [rangeSeries, setRangeSeries] = useState([]);
  const [rangeCafeTrend, setRangeCafeTrend] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(true);
  const [rangeError, setRangeError] = useState(null);

  // Fetch analytics data from API — same cross-tenant endpoints the Dashboard
  // page uses (fan out across every café's own DB), not the per-café /stats/*
  // endpoints those used to point at.
  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [dashboardRes, seriesRes] = await Promise.all([
        api.get('/internal/dashboard'),
        api.get('/internal/revenue-series', { params: { months: 12 } }),
      ]);

      setDashboard(dashboardRes.data || {});
      setFullRevenueSeries(seriesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics data');
      setDashboard({});
      setFullRevenueSeries([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Fetches everything the picked range needs at once: the platform-wide
  // monthly series (Revenue Trend chart) and the per-café breakdown for that
  // SAME window (Top Revenue Cafés + Café Revenue Trend table).
  const fetchRangeData = async (range, start, end) => {
    if (range === 'custom' && (!start || !end)) return;
    setRangeLoading(true);
    setRangeError(null);
    try {
      let params;
      if (range === 'custom') {
        // end is EXCLUSIVE server-side, so push it to the next midnight — a
        // user picking 1st-20th means "through the end of the 20th".
        const endNext = new Date(`${end}T00:00:00`);
        endNext.setDate(endNext.getDate() + 1);
        params = { start, end: endNext.toISOString().slice(0, 10) };
      } else {
        params = { months: RANGE_MONTHS[range] || 6 };
      }
      const [seriesRes, trendRes] = await Promise.all([
        api.get('/internal/revenue-series', { params }),
        api.get('/internal/cafe-revenue-trend', { params }),
      ]);
      setRangeSeries(seriesRes.data || []);
      setRangeCafeTrend(trendRes.data || []);
    } catch (err) {
      console.error('Failed to fetch revenue for range:', err);
      setRangeSeries([]);
      setRangeCafeTrend([]);
      setRangeError(
        err.response?.status === 404
          ? 'This endpoint is not available on the server yet — the backend needs to be updated.'
          : err.response?.data?.detail || 'Could not load revenue data for this range.'
      );
    } finally {
      setRangeLoading(false);
    }
  };

  // Custom range fetches only on explicit "Apply" (below), not per keystroke
  // — a request (and a re-render) on every completed intermediate date made
  // the year hard to retype, since re-editing an already-complete date can
  // report a "complete" value on the very first overwritten digit.
  useEffect(() => {
    if (analyticsRange !== 'custom') fetchRangeData(analyticsRange, customRangeStart, customRangeEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsRange]);

  const applyCustomAnalyticsRange = () => {
    if (customRangeStart && customRangeEnd) fetchRangeData('custom', customRangeStart, customRangeEnd);
  };

  // Month columns are driven by the first café's series — every café is
  // computed over the same buckets server-side, so they all line up.
  const trendMonths = rangeCafeTrend[0]?.months || [];
  // Only show the year in a column header when the range actually spans more
  // than one, so a normal 3/6/12-month view stays uncluttered.
  const trendSpansYears = new Set(trendMonths.map((m) => m.year)).size > 1;
  const monthLabel = (m) => (trendSpansYears ? `${m.month} ${String(m.year).slice(-2)}` : m.month);

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
  const formatCompact = (v) => {
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    return formatCurrency(v);
  };
  // Same year-disambiguation as the café trend table's monthLabel — only
  // matters for a range spanning more than one calendar year.
  const revenueSpansYears = new Set(rangeSeries.map((m) => m.year)).size > 1;
  const revenueData = rangeSeries.map((m) => ({
    ...m,
    label: revenueSpansYears ? `${m.month} ${String(m.year).slice(-2)}` : m.month,
  }));
  const yrr = fullRevenueSeries.reduce((s, m) => s + (m.revenue || 0), 0);
  const currentMrr = dashboard.monthly_revenue || 0;
  const prevMrr = fullRevenueSeries.length > 1
    ? fullRevenueSeries[fullRevenueSeries.length - 2].revenue
    : currentMrr;
  const mrrGrowth = prevMrr > 0 ? (((currentMrr - prevMrr) / prevMrr) * 100).toFixed(1) : '0.0';
  const totalCafes = dashboard.total_cafes || 0;
  const avgRevenuePerCafe = totalCafes > 0 ? currentMrr / totalCafes : 0;
  // Ranked by TOTAL revenue across the selected window (not just the last
  // bucket) — same "sum the months" approach Billing.jsx uses for its
  // per-café range totals.
  const topCafesForRange = [...rangeCafeTrend]
    .map((c) => ({ ...c, totalRevenue: (c.months || []).reduce((s, m) => s + (m.revenue || 0), 0) }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 5);
  const rangeLabelText = analyticsRange === 'custom'
    ? `${customRangeStart} to ${customRangeEnd}`
    : `last ${RANGE_MONTHS[analyticsRange]} months`;

  // Everything currently on screen, as one CSV: a summary block, the full
  // revenue trend, and the top-cafes list. Same escaping/download pattern as
  // AuditLogs.jsx's exportLogs, for consistency.
  const exportCsv = () => {
    const escapeCsv = (val) => {
      const s = String(val ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (cells) => cells.map(escapeCsv).join(',');

    const lines = [
      row(['Summary']),
      row(['Metric', 'Value']),
      row(['Monthly Recurring Revenue', currentMrr]),
      row(['MRR Growth % (vs last month)', mrrGrowth]),
      row(['Yearly Run Rate', yrr]),
      row(['Avg Revenue / Café', avgRevenuePerCafe.toFixed(2)]),
      row(['Total Cafés', totalCafes]),
      '',
      row([`Revenue Trend (${rangeLabelText})`]),
      row(['Month', 'Revenue']),
      ...rangeSeries.map((m) => row([m.month, m.revenue])),
      '',
      row([`Top Revenue Cafés (${rangeLabelText})`]),
      row(['Rank', 'Café Name', 'PCs', 'Revenue']),
      ...topCafesForRange.map((c, i) => row([i + 1, c.cafe_name, c.pc_count, c.totalRevenue])),
      '',
      row([`Café Revenue Trend (${rangeLabelText})`]),
      row(['Café', 'Month', 'Year', 'Revenue']),
      ...rangeCafeTrend.flatMap((c) => (c.months || []).map((m) => row([c.cafe_name, m.month, m.year, m.revenue]))),
    ];

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="analytics">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Business intelligence and financial insights</p>
        </div>
        <div className="header-actions">
          <button className="btn btn--secondary" onClick={fetchAnalytics} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} /> Refresh
          </button>
          {canExport && (
            <button className="btn btn--secondary" onClick={exportCsv} disabled={isLoading}>
              <Download size={16} /> Export
            </button>
          )}
          <div className="cafe-trend-controls">
            <select value={analyticsRange} onChange={(e) => setAnalyticsRange(e.target.value)} className="input select time-select">
              <option value="3m">Last 3 months</option>
              <option value="6m">Last 6 months</option>
              <option value="12m">Last 12 months</option>
              <option value="custom">Custom range</option>
            </select>
            {analyticsRange === 'custom' && (
              <div className="cafe-trend-range-picker">
                <input
                  type="date"
                  value={customRangeStart}
                  max={customRangeEnd || todayIso}
                  onChange={(e) => setCustomRangeStart(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomAnalyticsRange()}
                  aria-label="Start date"
                  className="input cafe-trend-range-picker__input"
                />
                <span className="cafe-trend-range-picker__sep">–</span>
                <input
                  type="date"
                  value={customRangeEnd}
                  min={customRangeStart || undefined}
                  max={todayIso}
                  onChange={(e) => setCustomRangeEnd(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyCustomAnalyticsRange()}
                  aria-label="End date"
                  className="input cafe-trend-range-picker__input"
                />
                <button
                  type="button"
                  className="btn btn--secondary cafe-trend-range-picker__apply"
                  onClick={applyCustomAnalyticsRange}
                  disabled={!customRangeStart || !customRangeEnd || rangeLoading}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="analytics-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="analytics-loading"><Loader2 size={28} className="spin" /></div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-card metric-card--hero glass-card">
              <div className="metric-card__icon"><DollarSign size={24} /></div>
              <div className="metric-card__content">
                <span className="metric-card__label">Monthly Recurring Revenue</span>
                <span className="metric-card__value">{formatCurrency(currentMrr)}</span>
                <div className={`metric-card__trend ${Number(mrrGrowth) >= 0 ? 'up' : 'down'}`}>
                  {Number(mrrGrowth) >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {mrrGrowth}% from last month
                </div>
              </div>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Yearly Run Rate</span>
              <span className="metric-card__value">{formatCompact(yrr)}</span>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Avg Revenue / Café</span>
              <span className="metric-card__value">{formatCompact(avgRevenuePerCafe)}</span>
            </div>
            <div className="metric-card glass-card">
              <span className="metric-card__label">Total Cafés</span>
              <span className="metric-card__value">{totalCafes}</span>
            </div>
          </div>

          {/* Charts */}
          <div className="chart-card glass-card revenue-trend-card">
            <h3 className="chart-card__title">Revenue Trend</h3>
            {rangeError ? (
              <div className="analytics-error" style={{ margin: 0 }}>
                <AlertTriangle size={16} /> {rangeError}
              </div>
            ) : rangeLoading ? (
              <div className="analytics-loading" style={{ padding: 'var(--space-6) 0' }}><Loader2 size={20} className="spin" /></div>
            ) : revenueData.length === 0 ? (
              <div className="coming-soon"><Clock size={20} /><span>No revenue data for this range.</span></div>
            ) : (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FE7B00" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#FE7B00" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="revenueStroke" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FE7B00" />
                        <stop offset="100%" stopColor="#F7302F" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} tickFormatter={formatCompact} />
                    <Tooltip contentStyle={{ background: 'rgba(26,26,36,0.95)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'white' }} formatter={(v) => [formatCurrency(v), 'Revenue']} />
                    <Area type="monotone" dataKey="revenue" stroke="url(#revenueStroke)" strokeWidth={2.5} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Bottom Grid */}
          <div className="bottom-grid">
            <div className="glass-card">
              <h3 className="card-title">Top Revenue Cafés</h3>
              {rangeLoading ? (
                <div className="analytics-loading" style={{ padding: 'var(--space-6) 0' }}><Loader2 size={20} className="spin" /></div>
              ) : topCafesForRange.length === 0 ? (
                <div className="coming-soon"><Clock size={20} /><span>No café revenue recorded for this range.</span></div>
              ) : (
                <div className="top-cafes">
                  {topCafesForRange.map((c, i) => (
                    <div key={c.cafe_id ?? i} className="top-cafe">
                      <span className="top-cafe__rank">{i + 1}</span>
                      <div className="top-cafe__info">
                        <span className="top-cafe__name">{c.cafe_name}</span>
                        <span className="top-cafe__pcs">{c.pc_count} PCs</span>
                      </div>
                      <div className="top-cafe__stats">
                        <span className="top-cafe__mrr">{formatCurrency(c.totalRevenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Café revenue trend — full width; a 12-month table needs the room. */}
          <div className="glass-card cafe-trend-card">
              <h3 className="card-title">Café Revenue Trend</h3>
              {rangeError ? (
                <div className="analytics-error" style={{ margin: 0 }}>
                  <AlertTriangle size={16} /> {rangeError}
                </div>
              ) : rangeLoading ? (
                <div className="analytics-loading" style={{ padding: 'var(--space-6) 0' }}><Loader2 size={20} className="spin" /></div>
              ) : rangeCafeTrend.length === 0 ? (
                <div className="coming-soon"><Clock size={20} /><span>No revenue data for this range.</span></div>
              ) : (
                <div className="cafe-trend-scroll">
                  {/* Inner max-height wrapper so a fleet with many cafés scrolls
                      vertically here instead of pushing the rest of the page
                      down — same maxHeight/overflowY pattern Billing.jsx uses
                      for its per-café tables. */}
                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                    <table className="cafe-trend-table">
                      <thead>
                        <tr>
                          <th className="cafe-trend-table__cafe">Café</th>
                          {trendMonths.map((m) => (
                            <th key={`${m.year}-${m.month}`}>{monthLabel(m)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rangeCafeTrend.map((c) => {
                          // Highlight each café's best month so the comparison the
                          // table exists for is readable at a glance. All-zero rows
                          // get no highlight — nothing "won".
                          const best = Math.max(...(c.months || []).map((m) => m.revenue || 0), 0);
                          return (
                            <tr key={c.cafe_id}>
                              <td className="cafe-trend-table__cafe">
                                <span className="cafe-trend-table__name">{c.cafe_name}</span>
                                <span className="cafe-trend-table__pcs">{c.pc_count} PCs</span>
                              </td>
                              {(c.months || []).map((m) => (
                                <td
                                  key={`${m.year}-${m.month}`}
                                  className={best > 0 && m.revenue === best ? 'cafe-trend-table__best' : undefined}
                                >
                                  {formatCurrency(m.revenue)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </div>
        </>
      )}

      <style>{`
        .analytics {
          /* noLag rebrand: scoped to this page only, same pattern as
             Sidebar/Header/Dashboard — other pages still read blue until
             they get the same treatment. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1300px;
        }

        /* Every card on this page — stat tiles, chart, tables — uses the
           same recipe in Figma (confirmed via get_design_context on
           2516:169/201/237/2567:3/2567:89, not assumed from Dashboard):
           pure backdrop-blur + white gradient sheen, no separate --glass-bg
           fill, border rgba(255,255,255,.18), radius 16px (not
           --radius-xl/18px). Scoped to .analytics so it doesn't leak into
           .glass-card usage on other pages. */
        .analytics .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          /* The base .glass-card class (index.css) sets no padding at all —
             .metric-card / .chart-card cover themselves with their own
             padding rule, but the bottom-grid "Top Revenue Cafés" and
             "Café Revenue Trend" cards use bare .glass-card with nothing
             else, so their heading/rows sat flush against the (rounded)
             edge with no inset. */
          padding: var(--space-6);
        }
        .analytics .glass-card::before { display: none; }

        .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-8); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }
        .header-actions { display: flex; gap: var(--space-3); align-items: center; flex-wrap: wrap; }
        .time-select { width: 160px; }

        .analytics-error { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-4); margin-bottom: var(--space-6); background: var(--status-danger-subtle); color: var(--status-danger); border-radius: var(--radius-md); font-size: var(--text-sm); }
        .analytics-loading { display: flex; justify-content: center; padding: var(--space-10) 0; color: var(--text-tertiary); }

        .coming-soon { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-6); color: var(--text-tertiary); font-size: var(--text-sm); }
        .coming-soon svg { flex-shrink: 0; }

        .metrics-grid { display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr; gap: var(--space-5); margin-bottom: var(--space-8); }
        .metric-card { padding: var(--space-6); }
        /* Figma's Analytics stat cards are visually identical — no special
           hero highlight on the MRR card (unlike this app's older blue/
           purple treatment). Matches the same call made on Dashboard.jsx's
           MRR card, confirmed here independently via 2516:169 vs 2516:176. */
        .metric-card--hero { display: flex; gap: var(--space-5); }
        .metric-card__icon { width: 56px; height: 56px; background: var(--accent-primary-subtle); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; color: var(--accent-primary); }
        .metric-card__content { flex: 1; }
        /* min-height reserves 2 lines regardless of actual line count — the
           hero card's "Monthly Recurring Revenue" wraps to 2 lines while
           the other three ("Yearly Run Rate" etc.) fit on 1, which without
           this left every card's value sitting at a different height. */
        .metric-card__label { display: block; font-size: var(--text-base); line-height: 1.4; min-height: 2.8em; font-weight: 400; color: var(--text-primary); margin-bottom: var(--space-2); }
        .metric-card__value { display: block; font-size: var(--text-3xl); font-weight: 600; letter-spacing: -0.02em; }
        .metric-card__trend { display: inline-flex; align-items: center; gap: 4px; font-size: var(--text-xs); font-weight: 500; margin-top: var(--space-2); }
        .metric-card__trend.up { color: var(--status-success); }
        .metric-card__trend.down { color: var(--status-danger); }

        .chart-card { padding: var(--space-6); }
        .revenue-trend-card { margin-bottom: var(--space-8); }
        .chart-card__title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-6); }
        .chart-container { margin: 0 calc(var(--space-3) * -1); }

        .bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-6); margin-bottom: var(--space-6); }
        .card-title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-5); }

        .top-cafes { display: flex; flex-direction: column; }
        .top-cafe { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-4) 0; border-bottom: 1px solid var(--divider); }
        .top-cafe:last-child { border-bottom: none; }
        .top-cafe__rank { width: 28px; height: 28px; background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; font-size: var(--text-xs); font-weight: 600; color: var(--text-tertiary); }
        .top-cafe__info { flex: 1; }
        .top-cafe__name { display: block; font-weight: 500; }
        .top-cafe__pcs { font-size: var(--text-xs); color: var(--text-tertiary); }
        .top-cafe__stats { text-align: right; }
        .top-cafe__mrr { display: block; font-family: var(--font-mono); font-weight: 500; }

        .cafe-trend-controls { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
        .cafe-trend-controls select, .cafe-trend-controls input { font-size: var(--text-xs); }
        .cafe-trend-range-picker { display: inline-flex; align-items: center; gap: 6px; }
        .cafe-trend-range-picker__input { font-size: var(--text-xs); width: 132px; padding: 6px 8px; }
        .cafe-trend-range-picker__sep { font-size: var(--text-xs); color: var(--text-tertiary); }
        .cafe-trend-range-picker__apply { padding: 6px 12px; font-size: var(--text-xs); }

        /* A 12-month range is wider than the card on smaller screens, so the
           table scrolls sideways inside its own container rather than pushing
           the page out. */
        .cafe-trend-scroll { overflow-x: auto; }
        .cafe-trend-table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
        .cafe-trend-table th { font-size: var(--text-xs); font-weight: 500; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.04em; text-align: right; padding: 0 var(--space-3) var(--space-3); white-space: nowrap; }
        .cafe-trend-table td { padding: var(--space-3); text-align: right; font-family: var(--font-mono); white-space: nowrap; border-top: 1px solid var(--divider); }
        .cafe-trend-table__cafe { text-align: left !important; font-family: var(--font-sans) !important; }
        .cafe-trend-table__name { display: block; font-weight: 500; }
        .cafe-trend-table__pcs { font-size: var(--text-xs); color: var(--text-tertiary); }
        .cafe-trend-table__best { color: var(--status-success); font-weight: 600; }

        @media (max-width: 1024px) { .metrics-grid { grid-template-columns: repeat(2, 1fr); } .bottom-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
